import { Action, Mutation, Module } from "vuex-class-modules";
import Axios, { AxiosResponse } from "axios";
import AxiosSecureModule from "./axiosSecureModule";
import { Log } from "@/plugins/Logging";
import { sensor } from "@/store";

/* ───────── countdown timer handle lives OUTSIDE the module instance ──────── */
let activeCountdown: ReturnType<typeof setInterval> | null = null;

/* ───────── types ───────── */
export type EnrollStep =
    | "idle" | "preparing" | "enrolling"
    | "configuring" | "success" | "timeout" | "failed";

@Module()
export default class EnrollModule extends AxiosSecureModule {
    /* ───────── reactive state ───────── */
    currentStep: EnrollStep = "idle";
    EnrolledDeviceTypeId: string | null = null;
    lastEnrolledDeviceTypeId: string | null = null;
    lastEnrollAt: number | null = null;
    countdown = 0;

    /* ───────── PUBLIC ACTIONS ───────── */

    @Action
    async startEnrollment(deviceTypeId: number) {
        if (this.currentStep !== "idle") return;

        Log.information(`EnrollModule: Starting enrollment (id=${deviceTypeId})`);
        await this.changeStep("preparing");
        this.setEnrolledDeviceTypeId(deviceTypeId.toString());
        this.setLastEnrolledDeviceTypeId(deviceTypeId.toString());
        this.setLastEnrollAt(Date.now());
        this.markDeviceConnecting();

        await this.requestBackendEnroll(deviceTypeId);
    }

    @Action joinOpen() { if (this.currentStep === "preparing") this.changeStep("enrolling"); }
    @Action startConfiguring() { if (this.currentStep === "enrolling") this.changeStep("configuring"); }

    @Action success() {
        if (!["enrolling", "configuring"].includes(this.currentStep)) return;
        this.stopCountdown();
        this.changeStep("success");
        this.clearDeviceConnecting();
    }

    @Action timeout() {
        if (!["preparing", "enrolling"].includes(this.currentStep)) return;
        this.stopCountdown();
        this.changeStep("timeout");
        this.clearDeviceConnecting();
    }

    @Action fail() {
        if (["idle", "failed"].includes(this.currentStep)) return;
        this.stopCountdown();
        this.changeStep("failed");
        this.clearDeviceConnecting();
    }

    @Action reset() {
        Log.information("EnrollModule: Reset");
        this.stopCountdown();
        this._setStep("idle");
        this.setCountdown(0);
        this.setEnrolledDeviceTypeId(null);
    }

    /** called by messageHub when JOIN_CLOSED arrives */
    @Action handleJoinClosed() {
        if (["preparing", "enrolling"].includes(this.currentStep)) this.timeout();
    }

    /* ───────── INTERNAL HELPERS ───────── */

    private async requestBackendEnroll(deviceTypeId: number) {
        await this.defineHeaderAxios();
        Axios.get(`api/sensor/enroll/${deviceTypeId}`)
            .then(() => Log.information("EnrollModule: backend request sent"))
            .catch(err => { Log.error("Backend enroll failed", err); this.fail(); });
    }

    private markDeviceConnecting() {
        if (this.EnrolledDeviceTypeId) sensor.SetConnectingState(this.EnrolledDeviceTypeId);
    }
    private clearDeviceConnecting() {
        if (this.EnrolledDeviceTypeId) {
            sensor.ClearConnectingState(this.EnrolledDeviceTypeId);
            this.setEnrolledDeviceTypeId(null);
        }
    }

    /* ───────── STEP CHANGE (action + pure mutation) ───────── */

    @Action async changeStep(step: EnrollStep) {
        this._setStep(step);      // state change
        this.configureCountdownFor(step);
    }

    @Mutation private _setStep(step: EnrollStep) {
        Log.information(`EnrollModule: ${this.currentStep} → ${step}`);
        this.currentStep = step;
    }

    /* ───────── COUNTDOWN LOGIC ───────── */

    private configureCountdownFor(step: EnrollStep) {
        this.stopCountdown();

        const secs =
            step === "preparing" ? 20 :
                step === "enrolling" ? 60 :
                    step === "configuring" ? 120 : 0;

        if (!secs) return;

        this.setCountdown(secs);

        activeCountdown = setInterval(() => {
            if (this.countdown > 0) this.decrementCountdown();
            else { this.stopCountdown(); this.timeout(); }
        }, 1000);
    }

    private stopCountdown() {
        if (activeCountdown) {
            clearInterval(activeCountdown);
            activeCountdown = null;
        }
    }

    /* ───────── PURE MUTATIONS ───────── */

    @Mutation private setCountdown(v: number) { this.countdown = v; }
    @Mutation private decrementCountdown() { if (this.countdown > 0) this.countdown--; }
    @Mutation private setEnrolledDeviceTypeId(id: string | null) { this.EnrolledDeviceTypeId = id; }
    @Mutation private setLastEnrolledDeviceTypeId(id: string | null) { this.lastEnrolledDeviceTypeId = id; }
    @Mutation private setLastEnrollAt(at: number | null) { this.lastEnrollAt = at; }
}